import { Layout, Text } from '@loadsmart/miranda-react'

import { Icon } from 'core/ui/components/icon/Icon'
import { Stars } from 'core/ui/components/stars/Stars'

import { useConfigContext } from 'config/ui/ConfigContext'

import { type FacilityReport } from '../domain/FacilityReport'
import {
  formatReviewCount,
  formatScoreNumber,
  getScorePercentage,
} from '../presenters/facilityPresenter'
import { ProgressBar } from './ProgressBar'

type FacilityRatingBarProps = {
  value: number
  label: string
}

function FacilityRatingBar({ value, label }: FacilityRatingBarProps) {
  return (
    <ProgressBar
      value={value}
      label={label}
      icon={<Icon name="star" color="neutral-lighter" size={14} />}
    />
  )
}

export type FacilityRatingProps = {
  report: FacilityReport
  inline?: boolean
}

export function FacilityRating({ report, inline = false }: FacilityRatingProps) {
  const { score, count, scoreCounts } = report
  const showFacilityScore = useConfigContext().showFacilityScore.toBoolean()

  if (!showFacilityScore || count === 0) return <></>

  if (inline)
    return (
      <Layout.Group align="center" gap="spacing-1">
        <Text variant="body-sm">Rating</Text>
        <Stars name="rating-breakdown" rating={score} />
        <Text variant="body-sm-bold">{formatScoreNumber(score)}</Text>
        <Text variant="body-sm">{formatReviewCount(count, inline)}</Text>
      </Layout.Group>
    )

  return (
    <Layout.Stack>
      <Text>Rating Breakdown</Text>
      <Layout.Group align="center">
        <Text variant="heading-lg-bold">{formatScoreNumber(score)}</Text>
        <Layout.Stack align="flex-start" gap="spacing-0-5">
          <Stars name="rating-breakdown" rating={score} />
          <Text variant="body-sm">{formatReviewCount(count)}</Text>
        </Layout.Stack>
      </Layout.Group>
      <Layout.Stack gap="spacing-1">
        <FacilityRatingBar value={getScorePercentage(count, scoreCounts['5'])} label="5" />
        <FacilityRatingBar value={getScorePercentage(count, scoreCounts['4'])} label="4" />
        <FacilityRatingBar value={getScorePercentage(count, scoreCounts['3'])} label="3" />
        <FacilityRatingBar value={getScorePercentage(count, scoreCounts['2'])} label="2" />
        <FacilityRatingBar value={getScorePercentage(count, scoreCounts['1'])} label="1" />
      </Layout.Stack>
    </Layout.Stack>
  )
}
